import { Injectable } from '@angular/core';
import { DateTimeType } from '../utils/date-time-helper';
import { WorkAssignment } from '../entities/WorkAssignment';
import { TimeRecord } from '../entities/timeRecord';
import { ProjectService } from './project.service';
import { UserService } from './user.service';
import { FieldActivityService } from './field-activity.service';
import { TaskService } from './task.service';
import { LaborCategoryService } from './labor-category.service';
import { DbTransaction } from '../models/db/db-transaction';
import { RecordNotFoundException } from '../utils/exceptions';

@Injectable({ providedIn: 'root' })
export class AssignmentService {
  constructor(
    private projectService: ProjectService,
    private userService: UserService,
    private fieldActivityService: FieldActivityService,
    private taskService: TaskService,
    private laborCategoryService: LaborCategoryService,
  ) {}

  async getWorkAssignmentFromTimeRecord(timeRecord: TimeRecord) {
    let fieldActivityId: number;
    try {
      const fieldActivity = await this.projectService.guessFieldActivity(timeRecord.EmployeeId, timeRecord.ProjectId, timeRecord.TaskId, timeRecord.LaborCategoryId);
      fieldActivityId = fieldActivity.Id;
    } catch {
      fieldActivityId = null;
    }
    return {
      ProjectId: timeRecord.ProjectId,
      TaskId: timeRecord.TaskId,
      LaborCategoryId: timeRecord.LaborCategoryId,
      FIPSStateId: timeRecord.FIPSStateId,
      FieldActivityId: fieldActivityId,
    };
  }

  async checkIsAssignmentValid(assignment: WorkAssignment | null, userId: number, targetDate: DateTimeType, existingTransaction?: DbTransaction): Promise<boolean> {
    if (!assignment || !assignment.FIPSStateId) {
      return false;
    }
    let assignedProjectIds = await this.projectService.getActiveAssignedProjectIds(userId, targetDate, existingTransaction);
    if (!assignedProjectIds.includes(assignment.ProjectId)) {
      return false;
    }
    if (!(await this.checkRelatedRecordsExist(assignment, existingTransaction))) {
      return false;
    }
    const project = await this.projectService.getProjectByOnlineId(assignment.ProjectId, existingTransaction);
    const user = await this.userService.getUserByOnlineId(userId, existingTransaction);
    if (user.IsFieldWorker && project.EnableFieldActivities) {
      let activeAssignedFieldActivitiesForProject = await this.projectService.getActiveAssignedFieldActivitiesByUserIdProjectId(user.Id, assignment.ProjectId, targetDate, existingTransaction);
      let selectedFieldActivity = activeAssignedFieldActivitiesForProject.find(
        assignedFieldActivity =>
          assignedFieldActivity.ProjectId === assignment.ProjectId &&
          assignedFieldActivity.TaskId === assignment.TaskId &&
          assignedFieldActivity.LaborCategoryId === assignment.LaborCategoryId &&
          assignedFieldActivity.Id === assignment.FieldActivityId
      );
      if (!selectedFieldActivity) {
        return false;
      }
    }
    if (
      !(await this.checkIsAssignmentProjectActive(assignment, targetDate, existingTransaction)) ||
      !(await this.checkIsAssignmentTaskActive(assignment, targetDate, existingTransaction)) ||
      !(await this.checkIsAssignmentLaborCategoryActive(assignment, targetDate, existingTransaction)) ||
      !(await this.checkIsAssignmentFieldActivityActive(assignment, targetDate, existingTransaction))
    ) {
      return false;
    }
    return true;
  }

  async checkWorkAssignmentsMatch(userId: number, assignment1: WorkAssignment | null, assignment2: WorkAssignment | null, existingTransaction?: DbTransaction): Promise<boolean> {
    if (
      assignment1?.ProjectId === assignment2?.ProjectId &&
      assignment1?.TaskId === assignment2?.TaskId &&
      assignment1?.LaborCategoryId === assignment2?.LaborCategoryId &&
      assignment1?.FIPSStateId === assignment2?.FIPSStateId
    ) {
      if (assignment1.FieldActivityId && assignment2.FieldActivityId) {
        return assignment1.FieldActivityId === assignment2.FieldActivityId;
      } else if (!assignment1.FieldActivityId && !assignment2.FieldActivityId) {
        return true;
      } else if (assignment1.FieldActivityId && !assignment2.FieldActivityId) {
        const matchingFieldActivities = await this.projectService.getMatchingAssignedFieldActivitiesForProject(
          userId,
          assignment2.ProjectId,
          assignment2.TaskId,
          assignment2.LaborCategoryId,
          existingTransaction
        );
        return matchingFieldActivities.some(fieldActivity => fieldActivity.Id === assignment1.FieldActivityId);
      } else if (!assignment1.FieldActivityId && assignment2.FieldActivityId) {
        const matchingFieldActivities = await this.projectService.getMatchingAssignedFieldActivitiesForProject(
          userId,
          assignment1.ProjectId,
          assignment1.TaskId,
          assignment1.LaborCategoryId,
          existingTransaction
        );
        return matchingFieldActivities.some(fieldActivity => fieldActivity.Id === assignment2.FieldActivityId);
      }
    }
    return false;
  }

  private async checkIsAssignmentProjectActive(assignment: WorkAssignment, targetDate: DateTimeType, existingTransaction?: DbTransaction): Promise<boolean> {
    const project = await this.projectService.getProjectByOnlineId(assignment.ProjectId, existingTransaction);
    const isActive = this.projectService.checkIsProjectActive(project, targetDate);
    return isActive;
  }

  private async checkIsAssignmentTaskActive(assignment: WorkAssignment, targetDate: DateTimeType, existingTransaction?: DbTransaction): Promise<boolean> {
    const task = await this.taskService.getTaskByOnlineId(assignment.TaskId, existingTransaction);
    const isActive = this.taskService.checkIsTaskActive(task, targetDate);
    return isActive;
  }

  private async checkIsAssignmentLaborCategoryActive(assignment: WorkAssignment, targetDate: DateTimeType, existingTransaction?: DbTransaction): Promise<boolean> {
    const laborCategory = await this.laborCategoryService.getLaborCategoryByOnlineId(assignment.LaborCategoryId, existingTransaction);
    const isActive = this.laborCategoryService.checkIsLaborCategoryActive(laborCategory, targetDate);
    return isActive;
  }

  private async checkIsAssignmentFieldActivityActive(assignment: WorkAssignment, targetDate: DateTimeType, existingTransaction?: DbTransaction): Promise<boolean> {
    if (assignment.FieldActivityId) {
      const fieldActivity = await this.fieldActivityService.getFieldActivityByOnlineId(assignment.FieldActivityId, existingTransaction);
      return true;
      // return this.fieldActivityService.checkIsFieldActivityActive(fieldActivity); // TODO: Figure out how IsActive should be incorporated
    } else {
      return true;
    }
  }

  async checkRelatedRecordsExist(assignment: WorkAssignment, existingTransaction?: DbTransaction): Promise<boolean> {
    try {
      const projectExists = await this.projectService.getProjectByOnlineId(assignment.ProjectId, existingTransaction);
      if (!projectExists) return false;
      const taskExists = await this.taskService.getTaskByOnlineId(assignment.TaskId, existingTransaction);
      if (!taskExists) return false;
      const laborCategoryExists = await this.laborCategoryService.getLaborCategoryByOnlineId(assignment.LaborCategoryId, existingTransaction);
      if (!laborCategoryExists) return false;
      if (assignment.FieldActivityId) {
        const fieldActivityExists = await this.fieldActivityService.getFieldActivityByOnlineId(assignment.FieldActivityId, existingTransaction);
        if (!fieldActivityExists) return false;
      }
      return true;
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        return false;
      } else {
        throw e;
      }
    }
  }
}
